'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { filledFill } from '@/lib/ui';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'default';
  icon?: ReactNode;
}

interface ConfirmContextType {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmDialogProvider');
  }
  return context;
}

const variantConfig = {
  danger: {
    icon: <Trash2 className="w-5 h-5 text-error" />,
    iconBg: 'bg-error-surface',
    confirmClass: `${filledFill('error')} focus-visible:ring-error`,
  },
  warning: {
    icon: <AlertTriangle className="w-5 h-5 text-warning" />,
    iconBg: 'bg-warning-surface',
    confirmClass: `${filledFill('warning')} focus-visible:ring-warning`,
  },
  default: {
    icon: <AlertTriangle className="w-5 h-5 text-text-secondary" />,
    iconBg: 'bg-cream-100',
    confirmClass: 'bg-warm-brown text-white hover:bg-warm-brown-hover',
  },
};

export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [dialogState, setDialogState] = useState<{
    options: ConfirmOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({ options, resolve });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    dialogState?.resolve(true);
    setDialogState(null);
  }, [dialogState]);

  const handleCancel = useCallback(() => {
    dialogState?.resolve(false);
    setDialogState(null);
  }, [dialogState]);

  const variant = dialogState?.options.variant || 'default';
  const config = variantConfig[variant];

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog
        open={!!dialogState}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center mb-2',
                config.iconBg
              )}
            >
              {dialogState?.options.icon || config.icon}
            </div>
            <AlertDialogTitle>{dialogState?.options.title}</AlertDialogTitle>
            <AlertDialogDescription>{dialogState?.options.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>
              {dialogState?.options.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm} className={config.confirmClass}>
              {dialogState?.options.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}
